import CBApi from "./CBApi";

class CBExchange extends CBApi {
  // Global pending cancel store
  static _pendingCancelIds = new Set();
  static _pendingListeners = new Set();

  static _emitPendingChange() {
    try {
      for (const cb of Array.from(CBExchange._pendingListeners)) {
        try { cb(CBExchange._pendingCancelIds); } catch {}
      }
    } catch {}
  }

  /** Subscribe to pending cancel changes. Returns an unsubscribe function. */
  static onPendingChange(cb) {
    if (typeof cb !== 'function') return () => {};
    CBExchange._pendingListeners.add(cb);
    return () => { try { CBExchange._pendingListeners.delete(cb); } catch {} };
  }

  /** Mark an order as pending cancel by its internal order id */
  static markPendingCancel(orderId) {
    if (!orderId) return;
    CBExchange._pendingCancelIds.add(String(orderId));
    CBExchange._emitPendingChange();
  }

  /** Remove pending cancel mark for given order id */
  static unmarkPendingCancel(orderId) {
    if (!orderId) return;
    CBExchange._pendingCancelIds.delete(String(orderId));
    CBExchange._emitPendingChange();
  }

  /** Check if an order id is pending cancel */
  static isPendingCancel(orderId) {
    if (!orderId) return false;
    return CBExchange._pendingCancelIds.has(String(orderId));
  }

  /** Utility to bulk cleanup any ids no longer present in current open orders */
  static cleanupPendingCancels(openOrders = []) {
    try {
      const existing = new Set((openOrders || []).map(o => String(o.id)));
      let changed = false;
      for (const id of Array.from(CBExchange._pendingCancelIds)) {
        if (!existing.has(id)) { CBExchange._pendingCancelIds.delete(id); changed = true; }
      }
      if (changed) CBExchange._emitPendingChange();
    } catch {}
  }

  /** `accountId` reads one account's series, for venues whose quotes differ per account (MT5). */
  static fetchOHLCVData(exchange, symbol, resolution, from, to, countBack, accountId) {
    let url = `exchange/ohlcv/${exchange}/${symbol}/${resolution}?since=${from}&to=${to}`;
    if (countBack && countBack > 0) {
      url += `&countBack=${countBack}`;
    }
    if (accountId) url += `&accountId=${encodeURIComponent(accountId)}`;
    return CBExchange.fetch(url);
  }

  /** Supported exchange types: `{ status, data: [{ id, name }] }`. */
  static fetchExchanges() {
    return CBExchange.fetch('exchange/list');
  }

  static fetchSymbols() {
    const url = `exchange/symbols`;
    return CBExchange.fetch(url);
  }

  /**
   * Symbol list of one exchange type. `accountId` asks for the list of one
   * account, for venues whose symbols differ per account (MT5).
   */
  static fetchSymbolsByExchange(exchangeType, accountId) {
    let url = `exchange/symbols?exchange=${encodeURIComponent(exchangeType)}`;
    if (accountId) url += `&accountId=${encodeURIComponent(accountId)}`;
    return CBExchange.fetch(url);
  }

  static fetchSymbolInfo(exchange, symbol) {
    const url = `exchange/symbol/${exchange}/${symbol}`;
    return CBExchange.fetch(url);
  }

  /**
   * Latest price of a symbol: `{ last }`, the close of the newest 1m candle
   * (NaN when there is none). The service has no ticker route; the candle
   * series is what the chart shows anyway.
   */
  static async fetchTicker(exchange, symbol, accountId) {
    const now = Math.floor(Date.now() / 1000);
    const resp = await CBExchange.fetchOHLCVData(exchange, symbol, '1m', now - 3600, now, undefined, accountId);
    const rows = resp?.status === 'ok' && Array.isArray(resp.data) ? resp.data : [];
    return { last: rows.length > 0 ? parseFloat(rows[rows.length - 1][4]) : NaN };
  }

  /** Fetch open positions for an account. */
  static fetchPositions(accountId) {
    return CBExchange.fetch(`exchange/positions/${accountId}`);
  }

  /**
   * Fetch all positions for the authenticated user from the position engine.
   * @param {object} [options]
   * @param {boolean} [options.all=false] - Include closed positions
   */
  static fetchTerminalPositions(options = {}) {
    const params = options.all ? '?all=true' : '';
    return CBExchange.fetch(`terminal/positions${params}`);
  }

  /**
   * Fetch orders linked to a specific position.
   * @param {string} positionId
   */
  static fetchPositionOrders(positionId) {
    return CBExchange.fetch(`terminal/positions/${encodeURIComponent(positionId)}/orders`);
  }

  /**
   * Close a terminal position on the exchange. The backend flattens it natively
   * (ccxt closePosition) where supported, otherwise via a reduce-only market
   * order, then finalizes the DB record and cleans up bracket legs.
   * @param {string} positionId
   * @param {'flatten'|'discard'} [mode='flatten'] flatten = close it on the
   *   exchange (native close or reduce-only market order) and finalize the
   *   record; discard = drop the record only, no order, no profit booked —
   *   for a position tracked wrongly or whose coins are gone.
   * @returns {Promise<object>} `{ status, mode: 'native' | 'reduceOnly' | 'discard' }`.
   */
  static closePosition(positionId, mode = 'flatten') {
    const query = mode === 'discard' ? '?mode=discard' : '';
    return CBExchange.fetch(`terminal/position/${encodeURIComponent(positionId)}${query}`, 'DELETE');
  }

  /** Fetch account balance (free / used / total per currency). */
  static fetchBalance(accountId) {
    return CBExchange.fetch(`account/${accountId}/balance`);
  }

  /**
   * Fetch executed (filled) order history for an account + symbol.
   * Used by the chart to render trade markers in terminal mode.
   * @param {string} accountId
   * @param {string} symbol - Trading pair (e.g. "BTC/USDC:USDC")
   * @param {number} [limit=200]
   * @returns {Promise<object|{error: string}>} `{ status, count, data }`
   */
  static async fetchOrderHistory(accountId, symbol, limit = 200) {
    try {
      const params = new URLSearchParams({
        accountId,
        symbol,
        limit: String(limit),
      });
      return await CBExchange.fetch(`terminal/order-history?${params.toString()}`);
    } catch (err) {
      return { error: err?.message || 'Failed to load order history' };
    }
  }

  static cancelOrder(accountId, symbol, exchangeOrderId, positionId) {
    const url = `exchange/order/cancel`;
    const params = {
      accountId: accountId,
      symbol: symbol,
      exchangeOrderId: exchangeOrderId,
      ...(positionId ? { positionId } : {}),
    }
    return CBExchange.fetch(url,'POST', params);
  }

  /**
   * Cancel every open order whose owner is named — the terminal's cancel-all.
   * The server reads the orders off the venue and their owner off the client
   * order id, so a bot's ladder is never touched unless its family is listed.
   * @param {object} opts
   * @param {string} [opts.accountId]  one account; every enabled one when omitted
   * @param {string} [opts.symbol]     one pair; every pair when omitted
   * @param {string[]} [opts.families] subset of terminal|external|botv3|smartbot|gridbot
   *                                   (default on the server: terminal + external)
   * @param {string[]} [opts.ownerIds] narrow to these bot/user ids
   * @param {boolean} [opts.dryRun]    resolve and count only, cancel nothing
   * @returns {Promise<object>} `{ status, data: { scanned, matched, skipped, groups, errors } }`
   */
  static cancelAllOrders({ accountId, symbol, families, ownerIds, dryRun } = {}) {
    const params = {
      ...(accountId ? { accountId } : {}),
      ...(symbol ? { symbol } : {}),
      ...(Array.isArray(families) ? { families } : {}),
      ...(Array.isArray(ownerIds) ? { ownerIds } : {}),
      ...(dryRun ? { dryRun: true } : {}),
    };
    return CBExchange.fetch('terminal/orders/cancel-all', 'POST', params);
  }

  /** Modify an existing order (e.g., change price or amount). */
  static modifyOrder(accountId, symbol, exchangeOrderId, price, amount) {
    const url = `exchange/order/edit`;
    const changes = {
      ...(typeof price === 'number' && !Number.isNaN(price) ? { price } : {}),
      ...(typeof amount === 'number' && !Number.isNaN(amount) ? { amount } : {}),
    };
    const params = { accountId, symbol, exchangeOrderId, changes };
    return CBExchange.fetch(url, 'POST', params);
  }

  /**
   * Create a new order on the exchange.
   * @param {string} accountId  - Account ID
   * @param {string} symbol     - Trading pair, e.g. "BTC/USDT"
   * @param {string} side       - 'buy' | 'sell'
   * @param {string} type       - 'limit' | 'market' | 'stop_limit' | 'stop_market'
   * @param {number} amount     - Order quantity
   * @param {number|null} price - Order price (required for limit/stop_limit, null for market)
   * @param {object} options    - Optional extras: stopLoss, takeProfit, trailingStop, autoCancelMinutes,
   *                              leverage, marginMode ('cross' | 'isolated').
   *                              Leverage is applied server-side via setLeverage before order placement.
   *                              Future: virtual, trailingBuy, targets[]
   */

  /**
   * Fetch PnL history snapshots for a position.
   * @param {string} positionId
   * @param {number} [points=50] - max data points
   */
  static async fetchPositionPnlHistory(positionId, points = 50) {
    try {
      return await CBExchange.fetch(`terminal/position/${encodeURIComponent(positionId)}/pnl?limit=${points}`);
    } catch (err) {
      return { error: err?.message || 'Failed to load P&L history' };
    }
  }

  static createOrder(accountId, symbol, side, type, amount, price, options = {}) {
    const url = `exchange/order/add`;
    const params = {
      accountId,
      symbol,
      side,
      type,
      amount: parseFloat(amount),
      ...(price !== null && price !== undefined && price !== ''
        ? { price: parseFloat(price) }
        : {}),
      ...(options.stopLoss ? { stopLoss: parseFloat(options.stopLoss) } : {}),
      ...(options.takeProfit ? { takeProfit: parseFloat(options.takeProfit) } : {}),
      // Execution type of the take profit once triggered: 'limit' (default) or 'market'
      ...(options.tpOrderType ? { tpOrderType: options.tpOrderType } : {}),
      ...(options.trailingStop ? { trailingStop: parseFloat(options.trailingStop) } : {}),
      ...(options.trailingStopMode ? { trailingStopMode: options.trailingStopMode } : {}),
      ...(options.stopTriggerPrice ? { stopTriggerPrice: parseFloat(options.stopTriggerPrice) } : {}),
      ...(options.reduceOnly ? { reduceOnly: true } : {}),
      ...(options.positionId ? { positionId: options.positionId } : {}),
      ...(options.autoCancelMinutes ? { autoCancelMinutes: parseInt(options.autoCancelMinutes, 10) } : {}),
      ...(options.leverage ? { leverage: parseInt(options.leverage, 10) } : {}),
      ...(options.marginMode ? { marginMode: options.marginMode } : {}),
      sync: true,
      timeoutMs: 15000,
    };
    return CBExchange.fetch(url, 'POST', params);
  }

  /**
   * Fetch pending virtual (event-triggered) orders for an account.
   * VOrders remain on the server until their indicator condition is satisfied,
   * at which point they are submitted as real orders automatically.
   * @param {string} [accountId] - Optional account filter.
   * @param {string} [symbol] - Optional symbol filter (e.g. "BTC/USDT").
   * @returns {Promise<Array|{error: string}>}
   */
  static async fetchVOrders(accountId, symbol) {
    try {
      const params = new URLSearchParams();
      if (accountId) params.set('accountId', accountId);
      if (symbol) params.set('symbol', symbol);
      const qs = params.toString();
      return await CBExchange.fetch(`terminal/vorders${qs ? `?${qs}` : ''}`);
    } catch (err) {
      return { error: err?.message || 'Failed to load virtual orders' };
    }
  }

  /**
   * Create a new virtual (event-triggered) order.
   * The order is held pending on the server until the event condition fires.
   * Throws on failure (network error or non-2xx response), like `createOrder` —
   * callers must catch and surface the error message to the user.
   * @param {string} accountId - Account ID to trade on.
   * @param {string} symbol - Trading pair (e.g. "BTC/USDT").
   * @param {'buy'|'sell'} side - Order side.
   * @param {'limit'|'market'|'stop_limit'|'stop_market'} type - Order type.
   * @param {number|string} amount - Base asset quantity.
   * @param {number|string|null} price - Limit price (null for market orders).
   * @param {object} [options={}] - Additional fields (e.g. eventGroups, stopLoss, takeProfit).
   * @returns {Promise<object>} Backend response, e.g. `{ status: 'ok', vOrderId }`.
   * @throws {Error} With the backend error message when creation fails.
   */
  static createVOrder(accountId, symbol, side, type, amount, price, options = {}) {
    return CBExchange.fetch('terminal/vorder', 'POST', {
      accountId, symbol, side, type,
      amount: parseFloat(amount),
      ...(price != null ? { price: parseFloat(price) } : {}),
      ...options,
    });
  }

  /**
   * Cancel a pending virtual order, preventing it from ever being submitted.
   * Throws on failure (network error or non-2xx response), like `createOrder` —
   * callers must catch and keep the row visible / notify the user.
   * @param {string} vOrderId - The `_id` of the VOrder to cancel.
   * @returns {Promise<object>} Backend response.
   * @throws {Error} With the backend error message when cancellation fails.
   */
  static cancelVOrder(vOrderId) {
    return CBExchange.fetch(`terminal/vorder/${encodeURIComponent(vOrderId)}`, 'DELETE');
  }

  /**
   * Create an SL/TP bracket group (entry + sl + tp legs as linked vOrders).
   * The backend picks native attached TP/SL where the exchange supports it,
   * otherwise the legs are watcher-emulated. Omit entryType to attach SL/TP
   * to an existing position (reduce-only close orders).
   * @param {object} input - { accountId, symbol, side, amount, entryType?,
   *   entryPrice?, slPrice?, tpPrice?, tpOrderType?, positionId?, leverage?,
   *   marginMode? } — tpOrderType: 'limit' (default) or 'market' execution
   *   of the TP leg once its trigger fires.
   * @returns {Promise<object>} `{ status, groupId, mode, legs, error? }`.
   * @throws {Error} With the backend error message when creation fails.
   */
  static createVOrderGroup(input) {
    return CBExchange.fetch('terminal/vorder-group', 'POST', input);
  }

  /**
   * Modify a single vOrder / bracket-group leg. Legs living as real exchange
   * orders are edited on the exchange; emulated legs are updated server-side.
   * @param {string} vOrderId
   * @param {object} changes - { price?, triggerValue?, amount? }
   * @returns {Promise<object>} Backend response.
   * @throws {Error} With the backend error message when the update fails.
   */
  static updateVOrder(vOrderId, changes) {
    return CBExchange.fetch(`terminal/vorder/${encodeURIComponent(vOrderId)}`, 'PUT', changes);
  }

  /**
   * Cancel all remaining legs of a bracket group (exchange orders included).
   * @param {string} groupId
   * @returns {Promise<object>} `{ status, canceled }`.
   * @throws {Error} With the backend error message when cancellation fails.
   */
  static cancelVOrderGroup(groupId) {
    return CBExchange.fetch(`terminal/vorder-group/${encodeURIComponent(groupId)}`, 'DELETE');
  }

  /**
   * Edit an existing bracket group in place (click-to-edit from the panel or a
   * dragged chart line). Structural changes (side/type) are realized by the
   * backend as cancel + recreate; price/amount edits use native exchange modify
   * where supported. `side` can only change while no position is running.
   * @param {string} groupId
   * @param {object} changes - {
   *     side?, entryType?, entryPrice?, amount?, leverage?, marginMode?,
   *     legOps?: [ {op:'edit', legId, price?, amount?}
   *              | {op:'delete', legId}
   *              | {op:'add', role:'sl'|'tp', price, amountPct, tpOrderType?} ] }
   * @returns {Promise<object>} `{ status, groupId, recreated?, legs? }`.
   * @throws {Error} With the backend error message when the update fails.
   */
  static updateVOrderGroup(groupId, changes) {
    return CBExchange.fetch(`terminal/vorder-group/${encodeURIComponent(groupId)}`, 'PUT', changes);
  }

  /**
   * List the active price-triggered cancel conditions of the user's still-
   * unfilled bracket groups (one per group). The chart draws a cancel-trigger
   * line from these. Threshold/reference prices are plain numbers.
   * @returns {Promise<object>} `{ status, data: [...] }`.
   */
  static getCancelConditions() {
    return CBExchange.fetch('terminal/cancel-conditions');
  }

  /**
   * Set a new ABSOLUTE threshold price on a bracket group's cancel condition
   * (chart line drag). The dragged value IS the new threshold; the bracket
   * group (entry/SL/TP) stays intact.
   * @param {string} groupId
   * @param {number} thresholdPrice - New absolute trigger price (> 0).
   * @param {'above'|'below'} [comparator] - Optional new comparator direction.
   * @returns {Promise<object>} `{ status, updated }`.
   */
  static updateCancelCondition(groupId, thresholdPrice, comparator) {
    const body = { thresholdPrice };
    if (comparator) body.comparator = comparator;
    return CBExchange.fetch(`terminal/vorder-group/${encodeURIComponent(groupId)}/cancel-condition`, 'PUT', body);
  }

  /**
   * Deactivate a bracket group's cancel condition (chart X button). Removes only
   * the cancel trigger; the bracket group itself stays intact.
   * @param {string} groupId
   * @returns {Promise<object>} `{ status, updated }`.
   */
  static removeCancelCondition(groupId) {
    return CBExchange.fetch(`terminal/vorder-group/${encodeURIComponent(groupId)}/cancel-condition`, 'DELETE');
  }

  /**
   * Fetch trade fill history for an account.
   * @param {string} accountId - The account to query.
   * @param {string} [symbol] - Optional symbol filter.
   * @param {object} [options={}] - Query options.
   * @param {number} [options.limit] - Maximum number of records to return.
   * @param {number} [options.since] - Unix timestamp (ms) to filter trades after this time.
   * @returns {Promise<Array|{error: string}>}
   */
  static async fetchTrades(accountId, symbol, options = {}) {
    try {
      const params = new URLSearchParams({ accountId });
      if (symbol) params.set('symbol', symbol);
      if (options.limit) params.set('limit', String(options.limit));
      if (options.since) params.set('since', String(options.since));
      return await CBExchange.fetch(`terminal/trades?${params.toString()}`);
    } catch (err) {
      return { error: err?.message || 'Failed to load trade history' };
    }
  }

  /**
   * Fetch active event subscriptions (indicator monitors) for an account.
   * Each event record describes an indicator condition being watched server-side
   * that will trigger one or more VOrders when the condition is met.
   * @param {string} accountId - The account to query.
   * @returns {Promise<Array|{error: string}>}
   */
  static async fetchEvents(accountId) {
    try {
      return await CBExchange.fetch(`terminal/events?accountId=${encodeURIComponent(accountId)}`);
    } catch (err) {
      return { error: err?.message || 'Failed to load events' };
    }
  }

  /**
   * Fetch recent order errors for a user, optionally filtered by account.
   * @param {string} [accountId] - Optional account filter.
   * @param {number} [limit=50] - Maximum errors to return.
   * @returns {Promise<Array|{error: string}>}
   */
  static async fetchOrderErrors(accountId, limit = 50) {
    try {
      let url = `terminal/order-errors?limit=${limit}`;
      if (accountId) url += `&accountId=${encodeURIComponent(accountId)}`;
      const res = await CBExchange.fetch(url);
      return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    } catch (err) {
      return { error: err?.message || 'Failed to load order errors' };
    }
  }

  /**
   * Fetch exchange-enforced trading pair limits and decimal precision from the terminal API.
   * Returns min/max constraints for amount, price, and cost, plus the number of decimal
   * places required for amount and price inputs.
   *
   * The pair goes in the query string, not the path: a symbol contains a slash
   * ("BTC/USDT"), and the encoded %2F never reaches the API — Apache answers it
   * with a 404 of its own, which the browser then reports as a CORS error.
   * @param {string} exchange - Exchange identifier, e.g. "Bybit".
   * @param {string} symbol - Trading pair symbol, e.g. "BTC/USDT".
   * @returns {Promise<{limits: {amount:{min,max}, price:{min,max}, cost:{min}}, precision:{amount,price}}|{error: string}>}
   */
  static async fetchPairLimits(exchange, symbol) {
    try {
      return await CBExchange.fetch(
        `terminal/pair-limits?exchangeId=${encodeURIComponent(exchange)}&symbol=${encodeURIComponent(symbol)}`,
      );
    } catch (err) {
      return { error: err?.message || 'Failed to load pair limits' };
    }
  }
}

export default CBExchange;